// frontend/admin-portal/src/pages/SupplierDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
} from "@fiberhome/shared-ui";
import { supplierService } from "../services/supplierService";
import SupplierModal from "../components/suppliers/SupplierModal";
import SupplierProductsPanel from "../components/suppliers/SupplierProductsPanel";

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const s = await supplierService.get(id);
      setSupplier(s);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <>
        <PageHeader title="Supplier" breadcrumbs={["Home", "Suppliers", "…"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !supplier) {
    return (
      <>
        <PageHeader
          title="Supplier not found"
          breadcrumbs={["Home", "Suppliers"]}
        />
        <Alert variant="danger">{error || "Supplier not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/suppliers")}
        >
          ← Back to Suppliers
        </Button>
      </>
    );
  }

  const fmtAddress = () => {
    const parts = [
      supplier.addressLine1,
      supplier.addressLine2,
      supplier.city,
      supplier.region,
      supplier.postalCode,
      supplier.country,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  };

  return (
    <>
      <PageHeader
        title={supplier.name}
        subtitle={supplier.code}
        breadcrumbs={["Home", "Suppliers", supplier.name]}
        actions={
          <div className="d-flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/suppliers")}
            >
              Back
            </Button>
          </div>
        }
      />

      <div className="row g-3 mb-3">
        <div className="col-lg-4">
          <Card title="Contact">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Status</span>
                <Badge variant={supplier.isActive ? "success" : "default"}>
                  {supplier.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Contact</span>
                <span>{supplier.contactName || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Phone</span>
                <span>{supplier.phone || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Email</span>
                <span>{supplier.email || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Website</span>
                {supplier.website ? (
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {supplier.website}
                  </a>
                ) : (
                  <span>—</span>
                )}
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Address</span>
                <span>{fmtAddress() || "—"}</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title="Business">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Tax ID</span>
                <span>{supplier.taxId || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Registration</span>
                <span>{supplier.registrationNo || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Payment Terms</span>
                <span>{supplier.paymentTermsDays} days</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Credit Limit</span>
                <span>
                  {Number(supplier.creditLimit || 0).toLocaleString()}{" "}
                  {supplier.currency}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Currency</span>
                <Badge variant="info">{supplier.currency}</Badge>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Rating</span>
                <span>
                  {"★".repeat(supplier.rating || 0)}
                  {"☆".repeat(5 - (supplier.rating || 0))}
                </span>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title="Banking">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">Bank</span>
                <span>{supplier.bankName || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Account</span>
                <span>{supplier.bankAccount || "—"}</span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">IBAN</span>
                <span>{supplier.bankIban || "—"}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {supplier.notes && (
        <Card title="Notes" className="mb-3">
          <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
            {supplier.notes}
          </div>
        </Card>
      )}

      <SupplierProductsPanel supplier={supplier} onChanged={load} />

      <SupplierModal
        open={editOpen}
        supplier={supplier}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />
    </>
  );
}
